import { TZDate } from '@date-fns/tz';
import {
  eachDayOfInterval,
  endOfMonth,
  format,
  isPast,
  isValid,
  parseISO,
  startOfMonth,
} from 'date-fns';
import { enUS, es, fr } from 'date-fns/locale';

import i18n from '@/constants/i18n';

/**
 * Get the date-fns locale from i18n language. The app ships en/es/fr, so a
 * French device must format dates in French, not fall through to Spanish.
 */
const getLocale = (locale: string = i18n.language) =>
  locale === 'fr' ? fr : locale === 'es' ? es : enUS;

export enum DateFormat {
  ISO = "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
  DATE_TIME = 'MMM d, yyyy, h:mm a',
  DATE_TIME_SAME_YEAR = 'MMM d, h:mm a',
  DATE_TIME_SHORT = 'MMM d, h:mma',
  DATE_SHORT = 'MMM d',
  TIME = 'h:mm a',
  DATE = 'MMM d, yyyy',
  MONTH_YEAR = 'MMM yyyy',
  FULL_MONTH_YEAR = 'MMMM yyyy',
  DAY_MONTH_FRIENDLY = 'MMMM dd',
  DAY_MONTH_FRIENDLY_SHORT = 'MMM dd',
  DAY_MONTH_YEAR = 'dd/MM/yyyy',
  DAY_MONTH_YEAR_TIME = 'dd/MM/yyyy h:mm a',
  DAY_MONTH_YEAR_TIME_SECONDS = 'dd/MM/yyyy h:mm:ss a',
  DAY_MONTH_YEAR_TIME_SECONDS_TIMEZONE = 'dd/MM/yyyy h:mm:ss a z',
  DAY_MONTH_YEAR_TIME_SECONDS_TIMEZONE_TIMEZONE = 'dd/MM/yyyy h:mm:ss a z z',
  DAY_MONTH_YEAR_TIME_SECONDS_TIMEZONE_TIMEZONE_TIMEZONE = 'dd/MM/yyyy h:mm:ss a z z z',
  DATE_ONLY = 'MMM d, yyyy',
  TIME_ONLY = 'h:mm a',
  FULL_DATE_TIME = 'MMM d, yyyy h:mm a',
  CART_ITEM_TIME = "MMM do 'at' h:mma",
  CART_ITEM_DATE = 'MMM d',
  CART_ITEM_DATE_TIME = 'MMM do h:mma',
  FULL_DATE = 'EEEE, MMM dd',
  WEEKDAY = 'EEEEE',
  WEEKDAY_UPPERCASE = 'EEE',
  MONTH_SHORT = 'MMM',
  DAY_NUMBER = 'd',
  USER_FRIENDLY_FULL_DATE = 'EEEE, MMMM d, yyyy',
  API_DATE = 'yyyy-MM-dd',
  WEEKDAY_FULL_DATE = 'EEEE, MMM d, yyyy',
  WEEKDAY_MONTH_DAY = 'EEE, MMM d',
}

/**
 * Parse a date string or Date object into a Date
 */
export const parseDate = (date: string | Date): Date => {
  if (date instanceof Date) {
    return date;
  }

  try {
    const parsed = parseISO(date);
    if (isValid(parsed)) {
      return parsed;
    }
    return new Date(date);
  } catch {
    return new Date();
  }
};

export const parseDateWithTimezone = (
  date: string | Date,
  timezone: string,
): Date => {
  const parsedDate = parseDate(date);
  return new TZDate(parsedDate, timezone);
};

/**
 * Format a date using the specified format
 */
export const formatDate = (
  date: string | Date,
  dateFormat: DateFormat,
  locale: string = i18n.language,
): string => {
  try {
    const parsedDate = parseDate(date);
    const formatted = format(parsedDate, dateFormat, {
      locale: getLocale(locale),
    });

    const [firstLetter, ...rest] = formatted.split('');
    return `${firstLetter.toUpperCase()}${rest.join('')}`;
  } catch {
    return 'Invalid date';
  }
};

/**
 * Format a date using the specified format in a specific timezone.
 * This is essential for displaying appointment times in the shop's timezone,
 * not the user's device timezone.
 *
 * @param date - The date string (typically UTC from backend) or Date object
 * @param dateFormat - The format to use
 * @param timezone - The IANA timezone string (e.g., 'America/New_York')
 * @param locale - The locale for formatting
 * @returns Formatted date string in the specified timezone
 */
export const formatDateWithTimezone = (
  date: string | Date,
  dateFormat: DateFormat,
  timezone: string,
  locale: string = i18n.language,
): string => {
  try {
    const parsedDate = parseDate(date);
    // Create a TZDate to represent the time in the target timezone
    // This ensures the time is displayed as it would appear in that timezone
    const tzDate = new TZDate(parsedDate, timezone);

    const formatted = format(tzDate, dateFormat, {
      locale: getLocale(locale),
    });

    const [firstLetter, ...rest] = formatted.split('');
    return `${firstLetter.toUpperCase()}${rest.join('')}`;
  } catch {
    return 'Invalid date';
  }
};

export function getMonthDateRange(
  date: Date,
  timezone?: string,
): { startDate: string; endDate: string } {
  const start = startOfMonth(date);
  const end = endOfMonth(date);

  const startDate = timezone ? parseDateWithTimezone(start, timezone) : start;
  const endDate = timezone ? parseDateWithTimezone(end, timezone) : end;

  return {
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd'),
  };
}

/**
 * "Today" as a local Date at midnight whose calendar day (Y/M/D) matches the
 * given timezone's current day. Returned as a plain local Date (not a TZDate)
 * so it composes with calendar grid math that compares local-day Dates via
 * `startOfDay`/`isSameDay`. Falls back to the device's local day when no
 * timezone is provided.
 */
export function getToday(timezone?: string | null): Date {
  const now = timezone ? new TZDate(new Date(), timezone) : new Date();
  // Use the timezone-aware accessors to read the shop's calendar day, then
  // rebuild it as a local midnight Date.
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function isDatePast(date: Date): boolean {
  return isPast(date);
}

export function isDatePastInTimezone(date: Date, timezone: string): boolean {
  return date < new TZDate(new Date(), timezone);
}

export function getTimePeriod(time: Date): 'morning' | 'afternoon' | 'evening' {
  const hours = time.getHours();
  if (hours < 12) return 'morning';
  if (hours < 18) return 'afternoon';
  return 'evening';
}

export function formatTimeSlot(time: Date): string {
  return format(time, 'h:mma').toLowerCase();
}

export function getDaysInMonth(date: Date): Date[] {
  const start = startOfMonth(date);
  const end = endOfMonth(date);
  return eachDayOfInterval({ start, end });
}

export function formatDuration(duration: number): string {
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  if (minutes === 0) return `${hours}h`;
  if (hours === 0) return `${minutes}min`;
  return `${hours}h, ${minutes}min`;
}
